// ─── ⭐⭐ IL FOGLIO: una griglia che si scrive e si trascina ─────────────────
//
// 🗣️ Disegnato da lui il 17/09/2026: *«se mi fai trattare questa tabella come
// se fosse un foglio Excel… io posso mettere una cifra in una casella, poi
// trascinare quella cifra in basso oppure di lato e riempire altre caselle. In
// questo modo vado velocemente a fare il listino»*.
//
// ⚖️ **Perché una griglia di caselle è più SICURA di un elenco di righe**, e
// non solo più comoda: un errore in una casella si VEDE — il colore cresce col
// prezzo, quindi una casella sbagliata in mezzo a una banda ha un'altra tinta.
// Una riga di listino sbagliata, in un elenco, non si vede affatto: si scopre
// alla cassa. ⇒ Qui la forma del dato e il modo di guardarlo sono la stessa
// cosa.
//
// 🔀 **E QUESTO FILE NON SA COSA PORTA**, dal 18/09/2026: il valore di una
// casella — un prezzo, un passo in minuti — lo sa la `Misura` che gli arriva
// da fuori. Qui dentro resta la meccanica, che è la parte difficile: scegliere,
// trascinare, riempire ripetendo il blocco, tornare indietro.
//
// ⛔ **Vuoto non è zero, e questo componente lo tiene separato fino in fondo**:
// una casella vuota non ha riga nel database e si legge «–»; lo zero si scrive
// apposta e nel listino si legge «gratis». Chi le confondesse segnerebbe
// partite gratis che nessuno rivede più.

import { useEffect, useRef, useState } from 'react';
import { GIORNI, ORDINE_SETTIMANA } from '@pmo/shared';
import type { Misura } from './misura.js';

/**
 * Una casella, come il foglio la vede: un numero, e — se serve a chi la usa —
 * uno **stato**.
 *
 * ⚠️ Lo `stato` è una parola che il foglio NON interpreta: la passa alla misura
 * e la mette fra le classi (`stato-spenta`). Serve alle fasce, dove una casella
 * può esserci ed essere **spenta** — cioè avere un valore che non vale. Il
 * listino non ne usa nessuno.
 */
export interface CellaFoglio {
  weekday: number;
  time: string;
  valore: number;
  stato?: string;
}

/** Una casella da scrivere: `null` la **cancella**, non ci mette zero. */
export interface DaScrivere {
  weekday: number;
  time: string;
  valore: number | null;
  stato?: string;
}

interface Punto {
  r: number;
  c: number;
}
interface Area {
  da: Punto;
  a: Punto;
}

const chiave = (weekday: number, time: string): string => `${weekday}|${time}`;

function normalizza(a: Area): { r1: number; r2: number; c1: number; c2: number } {
  return {
    r1: Math.min(a.da.r, a.a.r),
    r2: Math.max(a.da.r, a.a.r),
    c1: Math.min(a.da.c, a.a.c),
    c2: Math.max(a.da.c, a.a.c),
  };
}

export function Foglio({
  ore,
  celle,
  misura,
  modificabile,
  scrivi,
  annulla,
  quanteMosse,
  azioni,
  sottoTestata,
  evidenzia,
}: {
  ore: string[];
  celle: readonly CellaFoglio[];
  misura: Misura;
  modificabile: boolean;
  scrivi(daScrivere: DaScrivere[]): void;
  annulla(): void;
  quanteMosse: number;
  /**
   * Bottoni in più nella barra, che agiscono sulle caselle SCELTE.
   *
   * ⭐ È così che le fasce hanno «Spegni» e «Riaccendi» senza che il foglio
   * sappia cosa vuol dire spegnere: la selezione la conosce lui, il significato
   * lo conosce la schermata. 🗣️ E agisce sulla scelta e non su una casella
   * perché lui le spegne a gruppi: *«con un solo clic, anziché doverle ogni
   * volta ripianificare»* — un clic per una casella, un clic per trenta.
   */
  azioni?: (scelte: { weekday: number; time: string }[]) => React.ReactNode;
  /**
   * Una riga in più sotto il nome del giorno, nella testata della colonna.
   *
   * 🗣️ Nasce il 18/09/2026 dalle fasce per campo: guardando il foglio del
   * Campo 4 bisogna sapere quali giorni sono **suoi** e quali arrivano da
   * «tutti i campi». ⚖️ E va nella testata del GIORNO perché è lì che la cosa
   * è vera: la sostituzione è per giorno intero, non per casella — scriverla
   * dentro le caselle insegnerebbe che l'eccezione è un'ora, e non lo è.
   * 📌 *Un'informazione si mette alla granularità del fatto che descrive, o la
   *    schermata insegna una regola che il codice non applica.*
   */
  sottoTestata?: (weekday: number) => React.ReactNode;
  /**
   * 🤖 La casella da riempire, quando si arriva da una segnalazione
   * dell'assistente digitale (26/09/2026): si apre già SCELTA, segnata, e
   * visibile. Si scrive il prezzo come sempre.
   */
  evidenzia?: { weekday: number; time: string } | null;
}): React.JSX.Element {
  const dentro = new Map<string, CellaFoglio>();
  for (const c of celle) dentro.set(chiave(c.weekday, c.time), c);

  const puntoEvidenziato: Punto | null =
    evidenzia &&
    ore.includes(evidenzia.time) &&
    (ORDINE_SETTIMANA as readonly number[]).includes(evidenzia.weekday)
      ? {
          r: ore.indexOf(evidenzia.time),
          c: (ORDINE_SETTIMANA as readonly number[]).indexOf(evidenzia.weekday),
        }
      : null;
  const [area, setArea] = useState<Area>(
    puntoEvidenziato
      ? { da: puntoEvidenziato, a: puntoEvidenziato }
      : { da: { r: 0, c: 0 }, a: { r: 0, c: 0 } },
  );
  const evidenziata = useRef<HTMLTableCellElement>(null);
  useEffect(() => {
    evidenziata.current?.scrollIntoView({ block: 'center', inline: 'center' });
  }, []);
  const [modifica, setModifica] = useState<string | null>(null);
  /** Cosa si sta trascinando: la selezione, o il riempimento dal quadratino. */
  const [trascino, setTrascino] = useState<'niente' | 'selezione' | 'riempimento'>('niente');
  const [verso, setVerso] = useState<Punto | null>(null);
  const [valore, setValore] = useState('');
  const griglia = useRef<HTMLDivElement>(null);

  const sel = normalizza(area);
  const riempimento = verso === null ? null : normalizza({ da: area.da, a: verso });

  function cellaDi(r: number, c: number): CellaFoglio | undefined {
    return dentro.get(chiave(ORDINE_SETTIMANA[c]!, ore[r]!));
  }
  function valoreDi(r: number, c: number): number | undefined {
    return cellaDi(r, c)?.valore;
  }

  function celleDi(r1: number, r2: number, c1: number, c2: number, v: number | null): DaScrivere[] {
    const out: DaScrivere[] = [];
    for (let r = r1; r <= r2; r += 1) {
      for (let c = c1; c <= c2; c += 1) {
        out.push({ weekday: ORDINE_SETTIMANA[c]!, time: ore[r]!, valore: v });
      }
    }
    return out;
  }

  /**
   * ⭐ Il riempimento RIPETE il blocco di partenza, come farebbe un foglio di
   * calcolo: una casella sola si copia dappertutto, due righe diverse si
   * alternano. È la cosa che serve per un listino — la mattina e la sera che
   * si ripetono per sette giorni.
   */
  function applicaRiempimento(): void {
    if (riempimento === null) return;
    const alte = sel.r2 - sel.r1 + 1;
    const larghe = sel.c2 - sel.c1 + 1;
    const out: DaScrivere[] = [];
    for (let r = riempimento.r1; r <= riempimento.r2; r += 1) {
      for (let c = riempimento.c1; c <= riempimento.c2; c += 1) {
        if (r >= sel.r1 && r <= sel.r2 && c >= sel.c1 && c <= sel.c2) continue; // la partenza resta
        const v = valoreDi(
          sel.r1 + ((((r - sel.r1) % alte) + alte) % alte),
          sel.c1 + ((((c - sel.c1) % larghe) + larghe) % larghe),
        );
        out.push({ weekday: ORDINE_SETTIMANA[c]!, time: ore[r]!, valore: v ?? null });
      }
    }
    if (out.length > 0) scrivi(out);
    setArea({ da: { r: riempimento.r1, c: riempimento.c1 }, a: { r: riempimento.r2, c: riempimento.c2 } });
  }

  /*
   * ⚠️ Il tasto si può lasciare FUORI dalla griglia — sopra la barra, fuori
   * dalla finestra — e se il trascinamento finisse solo sulla casella sotto al
   * cursore resterebbe acceso per sempre: da lì in poi ogni passaggio del mouse
   * riempirebbe caselle che nessuno ha chiesto.
   */
  useEffect(() => {
    function su(): void {
      if (trascino === 'riempimento') applicaRiempimento();
      setTrascino('niente');
      setVerso(null);
    }
    window.addEventListener('mouseup', su);
    return () => window.removeEventListener('mouseup', su);
  });

  function conferma(testo: string, r: number, c: number): void {
    const v = testo.trim() === '' ? null : misura.leggi(testo);
    // ⛔ Ciò che non è un valore non si scrive e non si arrotonda a zero: la
    //    casella resta com'era, come fa il campo della data con una data che
    //    non esiste.
    if (testo.trim() !== '' && v === null) return;
    scrivi([{ weekday: ORDINE_SETTIMANA[c]!, time: ore[r]!, valore: v }]);
  }

  function tasti(e: React.KeyboardEvent): void {
    if (!modificabile) return;
    const { r, c } = area.a;
    const muovi = (dr: number, dc: number): void => {
      const p = { r: Math.max(0, Math.min(ore.length - 1, r + dr)), c: Math.max(0, Math.min(6, c + dc)) };
      setArea(e.shiftKey ? { da: area.da, a: p } : { da: p, a: p });
    };
    if (modifica !== null) {
      if (e.key === 'Enter') {
        conferma(modifica, r, c);
        setModifica(null);
        muovi(1, 0);
      } else if (e.key === 'Escape') {
        setModifica(null);
      } else if (e.key === 'Tab') {
        conferma(modifica, r, c);
        setModifica(null);
        muovi(0, e.shiftKey ? -1 : 1);
      } else return;
      e.preventDefault();
      return;
    }
    if (e.key === 'ArrowDown') muovi(1, 0);
    else if (e.key === 'ArrowUp') muovi(-1, 0);
    else if (e.key === 'ArrowLeft') muovi(0, -1);
    else if (e.key === 'ArrowRight') muovi(0, 1);
    else if (e.key === 'Enter')
      setModifica(valoreDi(r, c) === undefined ? '' : misura.battuto(valoreDi(r, c)!));
    else if (e.key === 'Delete' || e.key === 'Backspace')
      scrivi(celleDi(sel.r1, sel.r2, sel.c1, sel.c2, null));
    else if ((e.key === 'z' || e.key === 'Z') && (e.ctrlKey || e.metaKey)) annulla();
    else if (/^[0-9]$/.test(e.key)) setModifica(e.key);
    else return;
    e.preventDefault();
  }

  const quante = (sel.r2 - sel.r1 + 1) * (sel.c2 - sel.c1 + 1);
  /*
   * ⚠️ La scala la fa la MISURA, e le serve tutto il foglio: una tinta dice una
   * posizione («questa è delle alte»), e una posizione esiste solo dentro un
   * insieme. Passarle il solo valore la costringerebbe a inventarsi la scala.
   */
  const tutte = [...dentro.values()].map((c) => c.valore);

  return (
    <>
      {modificabile && (
        <div className="barra-foglio">
          <span className="tenue piccolo scelta-dice">
            {quante === 1
              ? `${GIORNI[ORDINE_SETTIMANA[sel.c1]!]} ${ore[sel.r1]}`
              : `${quante} caselle · ${GIORNI[ORDINE_SETTIMANA[sel.c1]!]} ${ore[sel.r1]} → ${GIORNI[ORDINE_SETTIMANA[sel.c2]!]} ${ore[sel.r2]}`}
          </span>
          <input
            inputMode={misura.modo}
            className="valore-foglio"
            placeholder={misura.segnaposto}
            aria-label={misura.etichettaBarra}
            value={valore}
            onChange={(ev) => setValore(ev.target.value)}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter' && misura.leggi(valore) !== null) {
                scrivi(celleDi(sel.r1, sel.r2, sel.c1, sel.c2, misura.leggi(valore)));
                ev.preventDefault();
              }
            }}
          />
          <button
            type="button"
            disabled={misura.leggi(valore) === null}
            onClick={() => scrivi(celleDi(sel.r1, sel.r2, sel.c1, sel.c2, misura.leggi(valore)))}
          >
            Scrivi {quante === 1 ? 'nella casella' : `nelle ${quante} caselle`}
          </button>
          <button
            type="button"
            className="piatto"
            onClick={() => scrivi(celleDi(sel.r1, sel.r2, sel.c1, sel.c2, null))}
          >
            Svuota
          </button>
          {/* ⭐ Tutta la colonna e tutta la riga: è il gesto «il lunedì è tutto
              così», che col trascinamento sarebbe una tirata di trenta caselle
              tenendo premuto — e su un telefono non si farebbe affatto. */}
          <button
            type="button"
            className="piatto"
            onClick={() => setArea({ da: { r: 0, c: sel.c1 }, a: { r: ore.length - 1, c: sel.c2 } })}
          >
            Tutta la colonna
          </button>
          <button
            type="button"
            className="piatto"
            onClick={() => setArea({ da: { r: sel.r1, c: 0 }, a: { r: sel.r2, c: 6 } })}
          >
            Tutta la riga
          </button>
          {azioni?.(
            (() => {
              const scelte: { weekday: number; time: string }[] = [];
              for (let r = sel.r1; r <= sel.r2; r += 1)
                for (let c = sel.c1; c <= sel.c2; c += 1)
                  scelte.push({ weekday: ORDINE_SETTIMANA[c]!, time: ore[r]! });
              return scelte;
            })(),
          )}
          <button type="button" className="piatto" disabled={quanteMosse === 0} onClick={annulla}>
            Annulla{quanteMosse > 0 ? ` (${quanteMosse})` : ''}
          </button>
        </div>
      )}

      <div
        className="foglio-scorre"
        ref={griglia}
        tabIndex={0}
        role="grid"
        aria-label={misura.etichettaGriglia}
        onKeyDown={tasti}
      >
        <table className={misura.saldaLeCorse === true ? 'foglio saldato' : 'foglio'}>
          <thead>
            <tr>
              <th />
              {ORDINE_SETTIMANA.map((g) => (
                <th key={g} scope="col">
                  {GIORNI[g]}
                  {sottoTestata?.(g)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ore.map((ora, r) => (
              <tr key={ora}>
                <th scope="row" className={ora.endsWith(':00') ? 'ora piena' : 'ora'}>
                  {ora}
                </th>
                {ORDINE_SETTIMANA.map((g, c) => {
                  const cella = dentro.get(chiave(g, ora));
                  const v = cella?.valore;
                  const scelta = r >= sel.r1 && r <= sel.r2 && c >= sel.c1 && c <= sel.c2;
                  const inRiempimento =
                    riempimento !== null &&
                    r >= riempimento.r1 &&
                    r <= riempimento.r2 &&
                    c >= riempimento.c1 &&
                    c <= riempimento.c2;
                  const angolo = scelta && r === sel.r2 && c === sel.c2;
                  const daRiempire =
                    puntoEvidenziato !== null && puntoEvidenziato.r === r && puntoEvidenziato.c === c;
                  return (
                    <td
                      key={g}
                      ref={daRiempire ? evidenziata : undefined}
                      className={[
                        'casella',
                        v === undefined ? 'vuota' : '',
                        daRiempire && v === undefined ? 'da-riempire' : '',
                        v === undefined ? '' : (misura.classe?.(v, cella?.stato) ?? ''),
                        scelta ? 'scelta' : '',
                        inRiempimento && !scelta ? 'in-riempimento' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      style={
                        v === undefined ? undefined : { background: misura.tinta(v, tutte, cella?.stato) }
                      }
                      title={misura.dice(v, GIORNI[g]!, ora, cella?.stato)}
                      onMouseDown={(ev) => {
                        if (!modificabile || ev.button !== 0) return;
                        const p = { r, c };
                        setModifica(null);
                        setArea(ev.shiftKey ? { da: area.da, a: p } : { da: p, a: p });
                        setTrascino('selezione');
                        /*
                         * 🚨 `preventScroll`, e NON è un dettaglio: dare il
                         * fuoco al foglio lo fa scorrere fin dove sta la
                         * casella, le caselle scivolano SOTTO al puntatore
                         * fermo, e ognuna manda il suo `mouseenter` — cioè la
                         * selezione si allunga da sola mentre il tasto è
                         * ancora giù. 📏 Visto a schermo: un clic su una
                         * casella ne sceglieva SEI.
                         * 📌 Muovere la pagina sotto a un puntatore fermo
                         *    equivale a muovere il puntatore: chi ascolta il
                         *    passaggio del mouse non sa distinguerli.
                         */
                        griglia.current?.focus({ preventScroll: true });
                      }}
                      onMouseEnter={() => {
                        if (trascino === 'selezione') setArea({ da: area.da, a: { r, c } });
                        else if (trascino === 'riempimento') setVerso({ r, c });
                      }}
                      onDoubleClick={() =>
                        modificabile && setModifica(v === undefined ? '' : misura.battuto(v))
                      }
                    >
                      {modifica !== null && r === area.a.r && c === area.a.c ? (
                        <input
                          autoFocus
                          className="scrivi-casella"
                          inputMode={misura.modo}
                          aria-label={`${misura.comeSiChiama} di ${GIORNI[g]} alle ${ora}`}
                          value={modifica}
                          onChange={(ev) => setModifica(ev.target.value)}
                          onBlur={() => {
                            conferma(modifica, r, c);
                            setModifica(null);
                          }}
                        />
                      ) : (
                        <>
                          <span>{v === undefined ? misura.niente : misura.breve(v, cella?.stato)}</span>
                          {/* ⭐ Il quadratino da cui si trascina, come nel foglio
                              di calcolo: sta sull'angolo in basso a DESTRA della
                              selezione, e solo lì.
                              🚨 La classe si chiama `maniglia-riempi` e NON
                              `maniglia`: quel nome è già della maniglia che
                              allunga una partita sul calendario (`left: 0;
                              right: 0; bottom: 0`), e le due regole si
                              sommavano — `left: 0` + `width: 8px` vince su
                              `right`, quindi il quadratino finiva in basso a
                              SINISTRA. 📌 *Due componenti che non si conoscono
                              possono dividersi un nome di classe senza che
                              nessun errore lo dica: lo dice solo l'occhio.* */}
                          {angolo && modificabile && (
                            <span
                              className="maniglia-riempi"
                              role="presentation"
                              onMouseDown={(ev) => {
                                ev.stopPropagation();
                                setTrascino('riempimento');
                                setVerso({ r, c });
                              }}
                            />
                          )}
                        </>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
